import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { useMemo, useState } from "react";
import { glossaryTerms } from "@/content/glossary";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { EabBannerBlock } from "@/components/eab-banner";

export const Route = createFileRoute("/hr-glossary/")({
  head: () => ({
    meta: [
      { title: "UK HR & Employment Law Glossary" },
      {
        name: "description",
        content:
          "Plain-English definitions for 100+ UK HR and employment law terms, from constructive dismissal to TUPE transfer.",
      },
      { property: "og:title", content: "UK HR & Employment Law Glossary" },
      { property: "og:description", content: "Plain-English definitions for the terms UK employers need to know." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-glossary" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-glossary" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://www.hrservicecentre.co.uk/hr-glossary#webpage",
          url: "https://www.hrservicecentre.co.uk/hr-glossary",
          name: "UK HR & Employment Law Glossary",
          description:
            "Plain-English definitions for 100+ UK HR and employment law terms, from constructive dismissal to TUPE transfer.",
          inLanguage: "en-GB",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
        }),
      },
      {

        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "EmployerHQ UK HR & Employment Law Glossary",
          url: "https://www.hrservicecentre.co.uk/hr-glossary",
          description:
            "Plain-English definitions for 100+ UK HR and employment law terms, written and reviewed by qualified advisors.",
          hasDefinedTerm: glossaryTerms.map((t) => ({
            "@type": "DefinedTerm",
            name: t.term,
            description: t.shortDefinition,
            url: `https://www.hrservicecentre.co.uk/hr-glossary/${t.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hrservicecentre.co.uk/hr-resources" },
            { "@type": "ListItem", position: 3, name: "Glossary", item: "https://www.hrservicecentre.co.uk/hr-glossary" },
          ],
        }),
      },
    ],
  }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () =>
      glossaryTerms
        .filter((t) => !q || t.term.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => a.term.localeCompare(b.term)),
    [q]
  );

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Resources", path: "/hr-resources" },
          { label: "Glossary", path: "/hr-glossary" },
        ]}
        eyebrow="Glossary"
        title="UK HR & Employment Law Glossary"
        subtitle="A plain-English HR and employment law glossary for UK employers."
        intro="Plain-English definitions for the terms UK employers need to know, written and reviewed by qualified advisors."
      />
      <section className="px-6 pt-8 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search terms…"
            className="w-full max-w-md rounded-md border border-border bg-card px-4 py-2.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </section>

      <section className="px-6 py-16 lg:px-8">
        <div className="mx-auto max-w-4xl divide-y divide-border rounded-2xl border border-border bg-card">
          {filtered.map((t) => (
            <Link
              key={t.slug}
              to="/hr-glossary/$slug"
              params={{ slug: t.slug }}
              className="group flex items-start justify-between gap-6 p-6 transition hover:bg-secondary/40"
            >
              <div>
                <h2 className="font-serif text-xl text-brand group-hover:text-accent">{t.term}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.shortDefinition}</p>
              </div>
              <span className="text-2xl text-muted-foreground group-hover:text-accent">→</span>
            </Link>
          ))}
        </div>
      </section>

      <EabBannerBlock membership source="glossary_hub" />

      <InternalLinksHub
        heading="Everything UK employers need from an HR partner"
        intro="Browse our most-requested services, sector pages and free resources, all written by senior HR and employment law specialists."
      />
    </>
  );
}
